import * as Sentry from '@sentry/react';
import {
  apiEndpointManager,
  isEndpointDownError,
} from '../services/apiEndpointManager';
import {
  getPositiveEvidenceCount,
  isBackendReachable,
  setBackendReachable,
} from './backendReachability';
import { isOnlineNow } from './connectivity';
import { probeReachabilitySoon } from './reachabilityHeartbeat';

/**
 * Cortacircuitos «no contesta ningún servidor».
 *
 * El modo «sin servidor» (`backendReachability` en `false`: pantallas desde la
 * caché, escrituras a la cola, banner, latido) ya existía, pero solo se entraba
 * por el TIEMPO: una lectura que no vuelve en 12 s o una escritura en 25 s. Un
 * fallo RÁPIDO —servidor caído detrás del proxy, conexión rechazada, DNS roto—
 * no dejaba nada dicho: cada consulta pagaba sus 5 intentos del `RetryLink`
 * (≈ 4,5 s) antes de enseñar lo guardado, y el listado de OTs encadena 8–10
 * consultas. Medido: 40–45 s por pantalla.
 *
 * Aquí se cuentan los fallos de red SEGUIDOS (los mismos que cuenta el
 * failover). Con 2, se confirma con `anyReachable()` —el mismo sondeo con el
 * que el latido SALE del modo— DOS veces, separadas 1,5 s. Un solo sondeo daba
 * falsos «sin servidor» (revisión del 9-oct): al despertar la tablet o pasar de
 * Wi-Fi a 4G todas las peticiones fallan a la vez y el primer `/health` cae en
 * plena transición; con un nodo muerto detrás del balanceador, un solo sondeo
 * es una moneda al aire. Y no se abre si, mientras se confirmaba, alguien vio
 * el servidor vivo, el aparato se quedó sin red o el WebView estuvo congelado.
 *
 * Al abrir se pide un sondeo de salida a los pocos segundos: si fue un falso
 * «sin servidor», dura eso y no los 30 s del latido.
 *
 * Spec: docs/superpowers/specs/2026-10-09-server-down-breaker-design.md
 */

/** Fallos de red seguidos que disparan la confirmación. */
export const FAILURES_TO_CONFIRM = 2;

/** Pausa entre los dos sondeos de confirmación. */
export const CONFIRM_GAP_MS = 1_500;

/**
 * Una confirmación normal dura como mucho 2 × 2,5 s + 1,5 s. Si tardó mucho
 * más, el WebView estuvo congelado (pantalla apagada) y su «no» es de antes.
 */
export const STALE_CONFIRM_MS = 15_000;

export interface ServerDownBreakerDeps {
  /** ¿Contesta el `/health` de algún servidor? Nunca rechaza. */
  anyReachable: () => Promise<boolean>;
  isBackendReachable: () => boolean | null;
  setBackendReachable: (reachable: boolean) => void;
  /** Cuántas veces se ha visto el servidor contestar (solo crece). */
  positiveEvidence: () => number;
  /** ¿Tiene red el aparato? Sin ella, un sondeo fallido no dice nada del servidor. */
  isDeviceOnline: () => boolean;
  sleep: (ms: number) => Promise<void>;
  now: () => number;
  onTrip?: () => void;
}

export interface ServerDownBreaker {
  /** Un intento fallido (lo llama el `retryIf` del `RetryLink`). */
  reportFailure(error: unknown): void;
  /** Una respuesta 200 (la llama `endpointHealthLink`). */
  reportSuccess(): void;
  /**
   * ¿Lo abrió el cortacircuitos y sigue sin verse el servidor? Un «sin
   * servidor» puesto por otro camino (techo de 12 s, sondeo antes de guardar)
   * NO cuenta: esos no comprobaron nada, y con ellos se sigue reintentando.
   */
  isOpen(): boolean;
}

export function createServerDownBreaker(
  deps: ServerDownBreakerDeps,
): ServerDownBreaker {
  let consecutiveFailures = 0;
  let confirming = false;
  // Prueba positiva vista justo al abrir: si sube, el servidor ha vuelto a
  // contestar y el corte deja de valer aunque otro camino ponga `false` luego.
  let trippedAtEvidence: number | null = null;

  const confirm = async (): Promise<void> => {
    confirming = true;
    const evidenceAtStart = deps.positiveEvidence();
    const startedAt = deps.now();
    const someoneSawTheServer = () => deps.positiveEvidence() !== evidenceAtStart;
    try {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        if (attempt > 0) await deps.sleep(CONFIRM_GAP_MS);
        if (someoneSawTheServer()) return;
        if (await deps.anyReachable()) {
          // Los fallos eran otra cosa (un corte suelto, la ruta /graphql rota
          // con el servidor vivo): se sigue reintentando como siempre.
          consecutiveFailures = 0;
          return;
        }
      }
      if (someoneSawTheServer()) return;
      if (!deps.isDeviceOnline()) return;
      if (deps.now() - startedAt > STALE_CONFIRM_MS) return;
      // Al abrir se empieza de cero: cuando se cierre, hará falta otra racha
      // entera para volver a abrir.
      consecutiveFailures = 0;
      deps.setBackendReachable(false);
      trippedAtEvidence = deps.positiveEvidence();
      deps.onTrip?.();
    } catch {
      // `anyReachable` no rechaza; si lo hiciera, no prueba nada: no se abre.
    } finally {
      confirming = false;
    }
  };

  return {
    reportFailure(error: unknown): void {
      if (!isEndpointDownError(error)) {
        // Un 4xx, un 500 o un error GraphQL: el servidor CONTESTÓ.
        consecutiveFailures = 0;
        return;
      }
      if (!deps.isDeviceOnline()) return;
      if (deps.isBackendReachable() === false) return;
      consecutiveFailures += 1;
      if (consecutiveFailures >= FAILURES_TO_CONFIRM && !confirming) {
        void confirm();
      }
    },
    reportSuccess(): void {
      consecutiveFailures = 0;
    },
    isOpen(): boolean {
      return (
        trippedAtEvidence !== null &&
        trippedAtEvidence === deps.positiveEvidence() &&
        deps.isBackendReachable() === false
      );
    },
  };
}

export const serverDownBreaker = createServerDownBreaker({
  anyReachable: () => apiEndpointManager.anyReachable(),
  isBackendReachable,
  setBackendReachable,
  positiveEvidence: getPositiveEvidenceCount,
  // Los dos: el estado nativo (APK) y `navigator.onLine`, porque con este en
  // `false` `anyReachable` contesta «no» sin preguntar a nadie.
  isDeviceOnline: () =>
    isOnlineNow() &&
    (typeof navigator === 'undefined' || navigator.onLine !== false),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
  now: () => Date.now(),
  onTrip: () => {
    // Miga y no evento: cada tablet sin servidor sería un evento, y la cuota de
    // Sentry ya va justa. Sirve para leer lo que pasó antes de un error.
    Sentry.addBreadcrumb({
      category: 'connectivity',
      level: 'warning',
      message:
        '[cortacircuitos] 2 fallos de red y dos /health sin respuesta — modo sin servidor',
    });
    probeReachabilitySoon();
  },
});
